import React, { useEffect, useState } from 'react';
import { supabase } from '@/config/supabase';
import { certificateService } from '@/services/certificateService';
import type { Certificate } from '@/types/exam';
import { Download, Share2, Copy, CheckCircle2 } from 'lucide-react';

interface CertificatePreviewProps {
  examAttemptId: string;
  onClose: () => void;
}

const CertificatePreview: React.FC<CertificatePreviewProps> = ({
  examAttemptId,
  onClose,
}) => {
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadCertificate();
  }, [examAttemptId]);

  const loadCertificate = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('certificates')
        .select('*')
        .eq('exam_attempt_id', examAttemptId)
        .maybeSingle(); // Use maybeSingle instead of single to handle 0 rows gracefully

      if (fetchError) throw fetchError;

      if (!data) {
        setError('Certificate not found. The certificate may still be generating.');
        return;
      }

      setCertificate(data as Certificate);
      setError(null);
    } catch (err) {
      console.error('Error loading certificate:', err);
      setError('Failed to load certificate.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!certificate?.certificate_url) {
      try {
        setGenerating(true);
        await certificateService.saveCertificate(certificate!);
        await loadCertificate();
      } catch (err) {
        console.error('Error generating certificate:', err);
        setError('Failed to generate certificate PDF.');
      } finally {
        setGenerating(false);
      }
      return;
    }

    try {
      const fileName = `${certificate.roadmap_name}_Certificate_${certificate.credential_code}.pdf`;
      await certificateService.downloadCertificate(certificate.certificate_url, fileName);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      setError('Failed to download certificate.');
    }
  };

  const handleCopyCredential = () => {
    if (certificate?.credential_code) {
      navigator.clipboard.writeText(certificate.credential_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareCredential = async () => {
    if (!certificate?.credential_code) return;

    const credentialUrl = `${window.location.origin}/verify-credential?code=${certificate.credential_code}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'NanoLež Certificate',
          text: `I earned a certificate for completing ${certificate.roadmap_name}!`,
          url: credentialUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(credentialUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const difficultyBadgeColor = {
    beginner: '#fbbf24', // amber
    intermediate: '#60a5fa', // blue
    expert: '#ec4899', // pink
  }[certificate?.roadmap_difficulty || 'beginner'];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
          <p className="text-center text-gray-600">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full">
          <p className="text-red-600 text-center mb-6">{error || 'Certificate not found.'}</p>
          <div className="flex gap-3">
            <button
              onClick={loadCertificate}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-700"
            >
              Retry
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-500 to-orange-500 text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <CheckCircle2 className="w-8 h-8" />
            Certificate of Achievement
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 px-4 py-2 rounded-lg"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Certificate Preview */}
          <div className="mb-8 bg-gradient-to-br from-white to-gray-50 rounded-lg border-4 border-red-600 p-12 text-center">
            <div className="mb-8">
              <h3 className="text-5xl font-bold text-gray-900 mb-2">Certificate</h3>
              <p className="text-gray-600 text-lg">of Achievement</p>
            </div>

            <p className="text-gray-700 text-lg mb-6">This is proudly presented to</p>

            <h2 className="text-4xl font-bold text-red-600 mb-8 uppercase tracking-wide">
              {certificate.user_name}
            </h2>

            <div className="bg-red-50 border-2 border-red-600 rounded-lg p-8 mb-8">
              <p className="text-gray-800 text-lg mb-4">
                For successfully completing the
              </p>
              <p className="text-3xl font-bold text-gray-900 mb-4">
                {certificate.roadmap_name}
              </p>
              <p className="text-lg text-gray-700">
                Assessment at{' '}
                <span
                  style={{ backgroundColor: difficultyBadgeColor }}
                  className="text-white px-4 py-1 rounded-full font-bold inline-block ml-2"
                >
                  {certificate.roadmap_difficulty.toUpperCase()}
                </span>{' '}
                Level
              </p>
            </div>

            <p className="text-2xl text-gray-900 mb-8">
              Achieved Score:{' '}
              <span className="text-red-600 font-bold">{certificate.score_percentage}%</span>
            </p>

            <div className="grid grid-cols-3 gap-4 text-center mb-8">
              <div>
                <p className="text-gray-600 text-sm font-semibold">Date of Issue</p>
                <p className="text-gray-900 font-semibold">
                  {new Date(certificate.issued_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-semibold">Credential ID</p>
                <p className="text-gray-900 font-mono font-bold">{certificate.credential_code}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-semibold">Status</p>
                <p className="text-green-600 font-semibold">✓ Verified</p>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              Issued by NanoLež Learning Platform
            </div>
          </div>

          {/* Certificate Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4">📋 Certificate Details</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-blue-600 font-semibold">Recipient</p>
                  <p className="text-blue-900">{certificate.user_name}</p>
                </div>
                <div>
                  <p className="text-blue-600 font-semibold">Roadmap</p>
                  <p className="text-blue-900">{certificate.roadmap_name}</p>
                </div>
                <div>
                  <p className="text-blue-600 font-semibold">Difficulty Level</p>
                  <p className="text-blue-900 capitalize">{certificate.roadmap_difficulty}</p>
                </div>
                <div>
                  <p className="text-blue-600 font-semibold">Score</p>
                  <p className="text-blue-900 font-bold">{certificate.score_percentage}%</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <h3 className="font-bold text-green-900 mb-4">🔐 Credential ID</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-green-600 font-semibold text-sm mb-2">Unique Code</p>
                  <div className="bg-white border border-green-300 rounded p-3 font-mono text-sm font-bold text-green-900 break-all">
                    {certificate.credential_code}
                  </div>
                </div>
                <p className="text-xs text-green-700">
                  This unique ID allows others to verify your certificate authenticity.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleDownloadCertificate}
              disabled={generating}
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" />
              {generating ? 'Generating PDF...' : 'Download Certificate PDF'}
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleCopyCredential}
                className="bg-blue-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-600 flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? 'Copied!' : 'Copy ID'}
              </button>

              <button
                onClick={handleShareCredential}
                className="bg-purple-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-purple-600 flex items-center justify-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">💡 Tip:</span> Share your credential ID with others
              or use it to verify your certificate authenticity. Your certificate is stored securely
              and can be accessed anytime.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t p-6">
          <button
            onClick={onClose}
            className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificatePreview;
